import { useAuthContext } from "@hooks/context/useAuthContext";
import { useProfileContext } from "@hooks/context/useProfileContext";
import useLocalStorage from "@hooks/utils/useLocalStorage";
import { createContext, ReactNode, useContext, useMemo } from "react";

import { IS_PROD_ENV } from "@app/bootstrap/init";

interface SupportLauncherContextType {
  // Whether the Soprano Assistant action should be offered. Mirrors the
  // `canChat` gate that used to live inline in GlobalAssistant: non-prod,
  // SocialPro admin, with a loaded org + profile.
  assistantEnabled: boolean;
  // The assistant drawer open-state, owned here so both the trigger (the
  // sidebar SpeedDial) and the drawer (GlobalAssistant) read/write the same
  // value without prop-drilling through every layout.
  assistantOpen: boolean;
  setAssistantOpen: (open: boolean) => void;
}

const SupportLauncherContext = createContext<
  SupportLauncherContextType | undefined
>(undefined);

export const SupportLauncherProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const { userOrganisation, userProfile } = useProfileContext();
  const { isSocialProAdmin } = useAuthContext();
  // Persist drawer open state so it survives layout-swap remounts triggered by
  // agent-initiated navigation. (Previously owned by GlobalAssistant.)
  const [assistantOpen, setAssistantOpen] = useLocalStorage(
    "globalAssistant.open",
    false
  );

  // MCP / agent surfaces: SocialPro admins only, and never in production.
  const assistantEnabled =
    !IS_PROD_ENV &&
    isSocialProAdmin &&
    Boolean(userOrganisation && userProfile);

  const value = useMemo(
    () => ({ assistantEnabled, assistantOpen, setAssistantOpen }),
    [assistantEnabled, assistantOpen, setAssistantOpen]
  );

  return (
    <SupportLauncherContext.Provider value={value}>
      {children}
    </SupportLauncherContext.Provider>
  );
};

export const useSupportLauncherContext = () => {
  const context = useContext(SupportLauncherContext);
  if (!context) {
    throw new Error(
      "useSupportLauncherContext must be used within a SupportLauncherProvider"
    );
  }
  return context;
};
